<div class="flex min-h-screen items-center justify-center px-4 py-8">
  <div hlmCard class="w-full max-w-xl">
    <div hlmCardHeader class="items-center text-center">
      <app-logo [widthInPx]="140" class="mb-2" />
      <h1 hlmCardTitle>Vervollständige dein Profil</h1>
      <p hlmCardDescription>
        @if (isMember) {
          Noch ein paar Details, dann kann dich {{ networkName || 'die Zentrale' }} als Mitglied
          freigeben
        } @else {
          Noch ein paar Details und du kannst loslegen
        }
      </p>
    </div>

    <div hlmCardContent class="space-y-6">
      <!-- ── Stepper ── -->
      <div class="space-y-2">
        <div class="flex items-center justify-between text-sm">
          <span hlmMuted>Schritt {{ currentStep + 1 }} von {{ steps.length }}</span>
          <span class="font-medium">{{ steps[currentStep] }}</span>
        </div>

        <div hlmProgress [value]="progressPercent">
          <div hlmProgressIndicator></div>
        </div>

        <!-- Step labels, desktop only: the bar alone carries the mobile layout -->
        <div class="text-muted-foreground hidden justify-between text-xs sm:flex">
          @for (label of steps; track label; let i = $index) {
            <span
              class="flex items-center gap-1"
              [class.text-foreground]="currentStep === i"
              [class.font-medium]="currentStep === i">
              @if (isStepCompleted(i)) {
                <ng-icon name="lucideCheck" />
              }
              {{ label }}
            </span>
          }
        </div>
      </div>

      <form [formGroup]="onboardingForm" (ngSubmit)="onSubmit()" class="space-y-4">
        <!-- ── Step 0: contact ── -->
        @if (currentStep === 0) {
          <div class="grid gap-4 sm:grid-cols-2">
            <div hlmField>
              <label hlmFieldLabel for="firstName">Vorname *</label>
              <input
                hlmInput
                id="firstName"
                type="text"
                formControlName="firstName"
                class="w-full"
                [attr.aria-invalid]="isInvalid('firstName') ? 'true' : null" />
              @if (isInvalid('firstName')) {
                <p hlmFieldDescription class="text-destructive">{{ errorFor('firstName') }}</p>
              }
            </div>

            <div hlmField>
              <label hlmFieldLabel for="lastName">Nachname *</label>
              <input
                hlmInput
                id="lastName"
                type="text"
                formControlName="lastName"
                class="w-full"
                [attr.aria-invalid]="isInvalid('lastName') ? 'true' : null" />
              @if (isInvalid('lastName')) {
                <p hlmFieldDescription class="text-destructive">{{ errorFor('lastName') }}</p>
              }
            </div>
          </div>

          <div hlmField>
            <label hlmFieldLabel for="phone">Telefonnummer *</label>
            <input
              hlmInput
              id="phone"
              type="tel"
              inputmode="tel"
              formControlName="phone"
              class="w-full"
              [attr.aria-invalid]="isInvalid('phone') ? 'true' : null" />
            @if (isInvalid('phone')) {
              <p hlmFieldDescription class="text-destructive">{{ errorFor('phone') }}</p>
            }
          </div>
        }

        <!-- ── Step 1: billing address ── -->
        @if (currentStep === 1) {
          <div hlmField>
            <label hlmFieldLabel for="companyName">Firmenname *</label>
            <input
              hlmInput
              id="companyName"
              type="text"
              formControlName="companyName"
              placeholder="z. B. Müller Gastronomie GmbH"
              class="w-full"
              [attr.aria-invalid]="isInvalid('companyName') ? 'true' : null" />
            @if (isInvalid('companyName')) {
              <p hlmFieldDescription class="text-destructive">{{ errorFor('companyName') }}</p>
            }
          </div>

          <div hlmField>
            <label hlmFieldLabel for="billingStreet">Straße und Hausnummer *</label>
            <input
              hlmInput
              id="billingStreet"
              type="text"
              formControlName="billingStreet"
              class="w-full"
              [attr.aria-invalid]="isInvalid('billingStreet') ? 'true' : null" />
            @if (isInvalid('billingStreet')) {
              <p hlmFieldDescription class="text-destructive">{{ errorFor('billingStreet') }}</p>
            }
          </div>

          <div class="grid gap-4 sm:grid-cols-[1fr_2fr]">
            <div hlmField>
              <label hlmFieldLabel for="billingZip">PLZ *</label>
              <input
                hlmInput
                id="billingZip"
                type="text"
                inputmode="numeric"
                formControlName="billingZip"
                class="w-full"
                [attr.aria-invalid]="isInvalid('billingZip') ? 'true' : null" />
              @if (isInvalid('billingZip')) {
                <p hlmFieldDescription class="text-destructive">{{ errorFor('billingZip') }}</p>
              }
            </div>

            <div hlmField>
              <label hlmFieldLabel for="billingCity">Stadt *</label>
              <input
                hlmInput
                id="billingCity"
                type="text"
                formControlName="billingCity"
                class="w-full"
                [attr.aria-invalid]="isInvalid('billingCity') ? 'true' : null" />
              @if (isInvalid('billingCity')) {
                <p hlmFieldDescription class="text-destructive">{{ errorFor('billingCity') }}</p>
              }
            </div>
          </div>

          <div hlmField>
            <label hlmFieldLabel for="billingCountry">Land *</label>
            <hlm-native-select
              selectId="billingCountry"
              class="w-full"
              formControlName="billingCountry">
              @for (country of billingCountries; track country.code) {
                <option hlmNativeSelectOption [value]="country.code">{{ country.name }}</option>
              }
            </hlm-native-select>
            @if (isInvalid('billingCountry')) {
              <p hlmFieldDescription class="text-destructive">{{ errorFor('billingCountry') }}</p>
            }
          </div>
        }

        <!-- ── Step 2: business address ── -->
        @if (currentStep === 2) {
          <div hlmItem variant="muted">
            <div hlmItemContent>
              <span hlmItemTitle>Gleiche Adresse wie Rechnungsadresse</span>
              <span hlmItemDescription>
                Deaktivieren, wenn dein Geschäft an einer anderen Adresse liegt.
              </span>
            </div>
            <div hlmItemActions>
              <hlm-switch
                aria-label="Gleiche Adresse wie Rechnungsadresse verwenden"
                formControlName="useBillingAsBusiness" />
            </div>
          </div>

          @if (!f['useBillingAsBusiness'].value) {
            <div hlmField>
              <label hlmFieldLabel for="businessStreet">Straße und Hausnummer *</label>
              <input
                hlmInput
                id="businessStreet"
                type="text"
                formControlName="businessStreet"
                class="w-full"
                [attr.aria-invalid]="isInvalid('businessStreet') ? 'true' : null" />
              @if (isInvalid('businessStreet')) {
                <p hlmFieldDescription class="text-destructive">{{ errorFor('businessStreet') }}</p>
              }
            </div>

            <div class="grid gap-4 sm:grid-cols-[1fr_2fr]">
              <div hlmField>
                <label hlmFieldLabel for="businessZip">PLZ *</label>
                <input
                  hlmInput
                  id="businessZip"
                  type="text"
                  inputmode="numeric"
                  formControlName="businessZip"
                  class="w-full"
                  [attr.aria-invalid]="isInvalid('businessZip') ? 'true' : null" />
                @if (isInvalid('businessZip')) {
                  <p hlmFieldDescription class="text-destructive">{{ errorFor('businessZip') }}</p>
                }
              </div>

              <div hlmField>
                <label hlmFieldLabel for="businessCity">Stadt *</label>
                <input
                  hlmInput
                  id="businessCity"
                  type="text"
                  formControlName="businessCity"
                  class="w-full"
                  [attr.aria-invalid]="isInvalid('businessCity') ? 'true' : null" />
                @if (isInvalid('businessCity')) {
                  <p hlmFieldDescription class="text-destructive">{{ errorFor('businessCity') }}</p>
                }
              </div>
            </div>

            <div hlmField>
              <label hlmFieldLabel for="businessCountry">Land *</label>
              <hlm-native-select
                selectId="businessCountry"
                class="w-full"
                formControlName="businessCountry">
                @for (country of countries; track country.code) {
                  <option hlmNativeSelectOption [value]="country.code">{{ country.name }}</option>
                }
              </hlm-native-select>
              @if (isInvalid('businessCountry')) {
                <p hlmFieldDescription class="text-destructive">
                  {{ errorFor('businessCountry') }}
                </p>
              }
            </div>
          }
        }

        <!-- ── Step 3 for members: the business name instead of a shop ── -->
        @if (currentStep === 3 && isMember) {
          <p hlmMuted class="text-sm">
            Als Mitglied {{ networkName ? 'von ' + networkName : 'des Netzwerks' }} brauchst du
            keinen eigenen Shop. Die Bankverbindung für deine monatlichen Auszahlungen hinterlegst
            du danach im Dashboard.
          </p>
          <div hlmField>
            <label hlmFieldLabel for="shopName">Name deines Betriebs *</label>
            <input
              hlmInput
              id="shopName"
              type="text"
              formControlName="shopName"
              placeholder="z. B. Café Müller"
              class="w-full"
              [attr.aria-invalid]="isInvalid('shopName') ? 'true' : null" />
            <p hlmFieldDescription>
              So erscheinst du bei der Zentrale und auf der Einlösestellen-Karte.
            </p>
            @if (isInvalid('shopName')) {
              <p hlmFieldDescription class="text-destructive">{{ errorFor('shopName') }}</p>
            }
          </div>
          <div hlmField>
            <label hlmFieldLabel for="memberWebsite">Website (optional)</label>
            <input
              hlmInput
              id="memberWebsite"
              type="url"
              formControlName="website"
              placeholder="www.beispiel.de"
              class="w-full"
              [attr.aria-invalid]="isInvalid('website') ? 'true' : null" />
            <p hlmFieldDescription>
              Erscheint in der Mitgliederliste deines Netzwerks. Auf deiner Website kannst du später
              auch den Gutschein-Shop des Netzwerks einbinden.
            </p>
            @if (isInvalid('website')) {
              <p hlmFieldDescription class="text-destructive">{{ errorFor('website') }}</p>
            }
          </div>
        }

        <!-- ── Step 3: shop ── -->
        @if (currentStep === 3 && !isMember) {
          <div hlmField>
            <label hlmFieldLabel for="shopName">Shop-/Markenname *</label>
            <input
              hlmInput
              id="shopName"
              type="text"
              formControlName="shopName"
              placeholder="z. B. Café Müller, Blumenladen Schmidt"
              class="w-full"
              [attr.aria-invalid]="isInvalid('shopName') ? 'true' : null" />
            <p hlmFieldDescription>Dieser Name erscheint auf Gutscheinen und im Gutschein-Shop.</p>
            @if (isInvalid('shopName')) {
              <p hlmFieldDescription class="text-destructive">{{ errorFor('shopName') }}</p>
            }
          </div>

          <div hlmField>
            <label hlmFieldLabel for="website">Website</label>
            <input
              hlmInput
              id="website"
              type="url"
              formControlName="website"
              placeholder="www.beispiel.de"
              class="w-full"
              [attr.aria-invalid]="isInvalid('website') ? 'true' : null" />
            <p hlmFieldDescription>Die Website erscheint auf Gutscheinen und im Gutschein-Shop.</p>
            @if (isInvalid('website')) {
              <p hlmFieldDescription class="text-destructive">{{ errorFor('website') }}</p>
            }
          </div>

          <div hlmField>
            <label hlmFieldLabel for="suburl">Deine persönliche Schenkwerk-URL *</label>
            <hlm-input-group class="w-full">
              <input
                hlmInputGroupInput
                id="suburl"
                type="text"
                formControlName="suburl"
                placeholder="businessname"
                [attr.aria-invalid]="isInvalid('suburl') ? 'true' : null" />
              <hlm-input-group-addon align="inline-end">
                <span hlmInputGroupText>.{{ getStorefrontBaseDomain() }}</span>
              </hlm-input-group-addon>
            </hlm-input-group>
            <p hlmFieldDescription>Nur Kleinbuchstaben, Zahlen und Bindestriche.</p>
            @if (isInvalid('suburl')) {
              <p hlmFieldDescription class="text-destructive">{{ errorFor('suburl') }}</p>
            }
          </div>
        }

        @if (error) {
          <div hlmAlert variant="destructive">
            <ng-icon name="lucideTriangleAlert" />
            <p hlmAlertDescription [innerHTML]="error"></p>
          </div>
        }

        <div class="flex items-center justify-between gap-2 pt-2">
          @if (currentStep > 0) {
            <button hlmBtn type="button" variant="outline" (click)="previousStep()">
              <ng-icon name="lucideArrowLeft" />
              Zurück
            </button>
          } @else {
            <span></span>
          }

          <button hlmBtn type="submit" [disabled]="loading">
            @if (loading) {
              <hlm-spinner />
              Wird gespeichert…
            } @else {
              {{ getButtonText() }}
              <ng-icon name="lucideArrowRight" data-icon="inline-end" />
            }
          </button>
        </div>
      </form>
    </div>
  </div>
</div>
